<template>
  <div class="follow-container">
    <!-- 表单区域 -->
    <div class="follow-form-section">
      <a-form-model ref="formRef" :model="form" :rules="rules" :colon="false">
        <div class="form-textarea-wrap">
          <a-textarea
            v-model="form.content"
            :rows="4"
            :maxLength="1000"
            class="follow-textarea"
            placeholder="1、跟进情况描述;2、存在的问题与风险;3、跟进的策略;4、下一步行动计划和目标;5、所需的资源"
          />
          <div class="textarea-counter">
            <span :class="{ 'counter-warning': form.content && form.content.length > 900 }">{{ (form.content || '').length }}</span>
            <span class="counter-total">/1000</span>
          </div>
        </div>

        <div class="form-actions-row">
          <div class="form-actions-left">
            <FileUpload @uploaded="onFileUploaded" moduleName="projectFollow">
              <a-button :disabled="fileList.length >= 5" class="btn-upload">
                <span class="upload-icon">+</span> 上传附件
              </a-button>
            </FileUpload>

            <a-form-model-item prop="followCategory" class="form-select-item">
              <a-select v-model="form.followCategory" placeholder="跟进分类" allow-clear class="form-select">
                <a-select-option v-for="item in followCategoryDict" :key="item.value" :value="item.value">{{ item.name }}</a-select-option>
              </a-select>
            </a-form-model-item>

            <a-form-model-item prop="followType" class="form-select-item">
              <a-select v-model="form.followType" placeholder="跟进方式" allow-clear class="form-select">
                <a-select-option v-for="item in followTypeDict" :key="item.value" :value="item.value">{{ item.name }}</a-select-option>
              </a-select>
            </a-form-model-item>

            <a-form-model-item prop="followTarget" class="form-select-item">
              <a-select v-model="form.followTarget" placeholder="跟进目标" allow-clear class="form-select">
                <a-select-option v-for="item in followTargetDict" :key="item.value" :value="item.value">{{ item.name }}</a-select-option>
              </a-select>
            </a-form-model-item>

            <a-form-model-item prop="followContact" class="form-input-item">
              <a-input v-model="form.followContact" placeholder="跟进联系人" class="form-input" />
            </a-form-model-item>
          </div>
          <div class="form-actions-right">
            <a-button type="primary" :loading="submitLoading" :disabled="!form.content || !form.content.trim()" class="btn-submit" @click="handleSubmit">跟进</a-button>
          </div>
        </div>
      </a-form-model>

      <!-- 附件列表 -->
      <div v-if="fileList.length > 0" class="file-list-section">
        <a-table
          :data-source="fileList"
          :columns="fileColumns"
          :pagination="false"
          row-key="id"
          size="small"
          class="file-table"
        >
          <span slot="name" slot-scope="text, record">
            <a :href="record.completePath" target="_blank" class="file-link">{{ record.name }}</a>
          </span>
          <span slot="action" slot-scope="text, record, index">
            <a href="#" @click.prevent="deleteFile(index)" class="file-delete">删除</a>
          </span>
        </a-table>
        <div v-if="fileList.length >= 5" class="file-limit-tip">最多上传5个文件</div>
      </div>
    </div>

    <div class="follow-divider"></div>

    <!-- Timeline 时间轴 -->
    <div class="timeline-section">
      <a-timeline v-if="timelineData.length > 0">
        <a-timeline-item v-for="item in timelineData" :key="item.id">
          <!-- 头部行：时间 + 标签 + 回复 -->
          <div class="tl-header">
            <div class="tl-header-left">
              <span class="tl-time-icon"></span>
              <span class="tl-time">{{ item.createTime ? item.createTime.replace('T', ' ').slice(0, 19) : '' }}</span>
              <span class="tl-category tl-category-daily" v-if="item.followCategory === 1">日常跟进</span>
              <span class="tl-category tl-category-plan" v-else-if="item.followCategory === 2">方案跟进</span>
            </div>
            <a class="tl-reply-btn" href="#" @click.prevent="toggleReply(item)">回复</a>
          </div>

          <!-- 内容卡片 -->
          <div class="tl-card">
            <div class="tl-card-accent"></div>
            <div class="tl-card-body">
              <div v-if="item.followType || item.targetName" class="tl-content-line">
                <span class="tl-content-text">
                  <span class="tl-author-name">{{ item.author }} </span>
                  <template v-if="item.followType">{{ item.followType }} 跟进了 </template>
                  <template v-if="item.followType">项目：{{ item.targetName }}</template>
                </span>
              </div>
              <div v-if="item.followTarget" class="tl-info-row">
                <span class="tl-info-label">跟进目标</span>
                <span class="tl-info-value">{{ item.followTarget }}</span>
              </div>
              <div v-if="item.followContact" class="tl-info-row">
                <span class="tl-info-label">跟进联系人</span>
                <span class="tl-info-value">{{ item.followContact }}</span>
              </div>
              <div v-if="item.content" class="tl-content-text tl-content-body" style="white-space: pre-wrap;">{{ item.content }}</div>
              <div v-if="item.fileList && item.fileList.length" class="tl-files">
                <a v-for="f in item.fileList" :key="f.id" :href="f.completePath" target="_blank" class="tl-file-tag">
                  <span class="file-icon">📎</span> {{ f.name }}
                </a>
              </div>
            </div>
            <div v-if="item.followStatus" class="tl-card-status">
              {{ item.followStatus }}
            </div>
          </div>

          <!-- 回复列表 -->
          <div v-if="item.followReplyList && item.followReplyList.length > 0" class="tl-replies">
            <div v-for="reply in item.followReplyList" :key="reply.id" class="tl-reply-item">
              <div class="tl-reply-header">
                <div class="reply-info">
                  <span class="reply-user">{{ reply.author }}</span>
                  <span class="reply-target">回复 {{ reply.replyTargetUserName }}</span>
                  <span class="reply-time">{{ reply.createTime ? reply.createTime.replace('T', ' ').slice(0, 19) : '' }}</span>
                </div>
                <a class="reply-action" href="#" @click.prevent="toggleReply(item, reply)">回复</a>
              </div>
              <div class="reply-content">{{ reply.replyContent }}</div>
            </div>
          </div>

          <!-- 回复输入框 -->
          <div v-if="item._showReply" class="tl-reply-form">
            <div v-if="item._replyTargetUserName" class="tl-reply-target">
              <span class="reply-target-icon">@</span> 回复 {{ item._replyTargetUserName }}
            </div>
            <a-textarea v-model="item._replyContent" :rows="2" placeholder="请输入回复内容" class="reply-textarea" />
            <div class="reply-form-actions">
              <a-button type="primary" size="small" :loading="item._replyLoading" @click="handleReply(item)">提交回复</a-button>
              <a-button size="small" @click="toggleReply(item)" class="reply-cancel-btn">取消</a-button>
            </div>
          </div>
        </a-timeline-item>
      </a-timeline>
      <a-empty v-else description="暂无跟进记录" class="timeline-empty" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getFollowList, addFollow, addFollowReply } from "@/api/market/follow";
import FileUpload from "@/components/file-upload.vue";

export default {
  components: { FileUpload },
  props: {
    targetId: { type: [Number, String], default: null },
    targetType: { type: String, default: "project" }
  },
  data() {
    return {
      submitLoading: false,
      fileList: [],
      fileColumns: [
        { title: "文件名称", dataIndex: "name", scopedSlots: { customRender: "name" } },
        { title: "操作", dataIndex: "action", width: 80, scopedSlots: { customRender: "action" } }
      ],
      form: {
        followCategory: undefined,
        followType: undefined,
        followTarget: undefined,
        followContact: "",
        content: ""
      },
      rules: {},
      timelineData: []
    };
  },
  computed: {
    ...mapGetters("setting", ["findDataDict"]),
    followCategoryDict() { return this.findDataDict("scrm.scrm_follow_category"); },
    followTypeDict() { return this.findDataDict("scrm.scrm_follow_type"); },
    followTargetDict() { return this.findDataDict("scrm.scrm_follow_target"); }
  },
  mounted() {
    this.loadTimeline()
  },
  methods: {
    loadTimeline() {
      if (!this.targetId) return;
      getFollowList({
        targetType: this.targetType,
        targetId: this.targetId
      }).then((res) => {
        this.timelineData = (res || []).sort((a, b) => new Date(b.createTime) - new Date(a.createTime));
      });
    },

    onFileUploaded(list) {
      if (this.fileList.length + list.length > 5) {
        this.$message.warning("最多上传5个文件");
        const remaining = 5 - this.fileList.length;
        this.fileList = [...this.fileList, ...list.slice(0, remaining)];
      } else {
        this.fileList = [...this.fileList, ...list];
      }
    },

    deleteFile(index) {
      this.fileList.splice(index, 1);
    },

    handleSubmit() {
      if (!this.form.content || !this.form.content.trim()) {
        this.$message.warning("请输入跟进内容");
        return;
      }
      this.submitLoading = true;
      const fileData = this.fileList.length > 0 ? JSON.stringify(this.fileList.map(f => ({ id: f.id }))) : null;
      addFollow({
        targetType: this.targetType,
        targetId: this.targetId,
        followCategory: this.form.followCategory,
        followType: Array.isArray(this.form.followType) ? this.form.followType.join(",") : this.form.followType,
        followTarget: this.form.followTarget,
        followContact: this.form.followContact,
        contentType: 1,
        content: this.form.content,
        files: fileData
      }).then(() => {
        this.$message.success("跟进记录添加成功");
        this.resetForm();
        this.loadTimeline();
        this.$emit('refresh');
      }).finally(() => {
        this.submitLoading = false;
      });
    },

    toggleReply(item, reply) {
      const show = !item._showReply;
      this.$set(item, "_showReply", show);
      if (show) {
        this.$set(item, "_replyContent", "");
        if (reply) {
          this.$set(item, "_replyTargetUserId", reply.createBy);
          this.$set(item, "_replyTargetUserName", reply.author);
        }
      } else {
        this.$set(item, "_replyTargetUserId", null);
        this.$set(item, "_replyTargetUserName", null);
      }
    },

    handleReply(item) {
      console.log("item===",item)
      const content = item._replyContent;
      if (!content || !content.trim()) {
        this.$message.warning("请输入回复内容");
        return;
      }
      this.$set(item, "_replyLoading", true);
      addFollowReply({
        followId: item.id,
        replyContent: content,
        replyTargetUserId: item.createBy,
        replyTargetUserName: item.author
      }).then(() => {
        this.$message.success("回复成功");
        this.loadTimeline();
      }).finally(() => {
        this.$set(item, "_replyLoading", false);
      });
    },

    resetForm() {
      this.form = {
        followCategory: undefined,
        followType: [],
        followTarget: undefined,
        followContact: "",
        content: ""
      };
      this.fileList = [];
    }
  }
};
</script>

<style lang="less" scoped>
.follow-container {
  padding: 0;
}

/* ========== 表单区域 ========== */
.follow-form-section {
  padding: 0 24px;
}

.form-textarea-wrap {
  margin-bottom: 12px;
  position: relative;

  ::v-deep .ant-input {
    border-radius: 8px;
    border: 1px solid #d9d9d9;
    font-size: 13px;
    line-height: 1.6;
    transition: all 0.3s;

    &:focus {
      border-color: #1890ff;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
    }

    &::placeholder {
      color: #bfbfbf;
      line-height: 1.8;
    }
  }
}

.textarea-counter {
  position: absolute;
  bottom: 8px;
  right: 12px;
  font-size: 12px;
  color: #bfbfbf;
  pointer-events: none;
  user-select: none;

  .counter-warning {
    color: #ff4d4f;
  }

  .counter-total {
    color: #d9d9d9;
  }
}

.form-actions-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.form-actions-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
}

.form-actions-right {
  flex-shrink: 0;
}

.btn-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  background: #fafafa;
  color: #595959;
  font-size: 13px;
  height: 32px;
  padding: 0 12px;
  transition: all 0.3s;

  &:hover {
    border-color: #1890ff;
    color: #1890ff;
    background: #e6f7ff;
  }

  .upload-icon {
    display: inline-block;
    font-size: 14px;
    font-weight: 600;
    margin-right: 2px;
  }
}

.form-select-item {
  margin-bottom: 0 !important;

  ::v-deep .ant-form-item-control-wrapper {
    line-height: 32px;
  }
}

.form-select {
  width: 140px;
  height: 32px;

  ::v-deep .ant-select-selection {
    border-radius: 6px;
    height: 32px;
    line-height: 30px;
    font-size: 13px;
    border-color: #d9d9d9;
    transition: all 0.3s;

    &:hover {
      border-color: #1890ff;
    }
  }

  ::v-deep .ant-select-selection__placeholder {
    color: #bfbfbf;
  }
}

.form-input-item {
  margin-bottom: 0 !important;

  ::v-deep .ant-form-item-control-wrapper {
    line-height: 32px;
  }
}

.form-input {
  width: 160px;
  height: 32px;
  border-radius: 6px;
  font-size: 13px;
  border-color: #d9d9d9;
  transition: all 0.3s;

  &:focus {
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
  }

  &::placeholder {
    color: #bfbfbf;
  }
}

.btn-submit {
  height: 32px;
  padding: 0 20px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  border: none;
  box-shadow: 0 2px 4px rgba(24, 144, 255, 0.3);
  transition: all 0.3s;

  &:hover {
    background: linear-gradient(135deg, #40a9ff 0%, #1890ff 100%);
    box-shadow: 0 4px 8px rgba(24, 144, 255, 0.4);
  }
}

/* ========== 附件列表 ========== */
.file-list-section {
  margin-top: 12px;
}

.file-table {
  border-radius: 6px;
  overflow: hidden;

  ::v-deep .ant-table {
    border: 1px solid #f0f0f0;
    border-radius: 6px;
  }

  ::v-deep .ant-table-thead > tr > th {
    background: #fafafa;
    font-size: 12px;
    color: #8c8c8c;
    font-weight: 500;
  }

  ::v-deep .ant-table-tbody > tr > td {
    font-size: 12px;
  }
}

.file-link {
  color: #1890ff;
  text-decoration: none;
  transition: color 0.3s;

  &:hover {
    color: #40a9ff;
    text-decoration: underline;
  }
}

.file-delete {
  color: #ff4d4f;
  transition: color 0.3s;

  &:hover {
    color: #ff7875;
  }
}

.file-limit-tip {
  color: #bfbfbf;
  font-size: 12px;
  margin-top: 6px;
  padding-left: 4px;
}

/* ========== 分隔线 ========== */
.follow-divider {
  height: 1px;
  background: linear-gradient(to right, transparent, #e8e8e8, transparent);
  margin: 16px 0;
}

/* ========== 时间轴区域 ========== */
.timeline-section {
  margin-top: 8px;
  padding: 8px 36px 16px;
  height: 400px;
  overflow-y: auto;

  .timeline-title {
    margin-bottom: 16px;
    font-size: 14px;
    font-weight: 600;
    color: #262626;
  }
}

.timeline-empty {
  padding: 24px 0;
}

// 覆盖 ant-design timeline 圆点颜色
::v-deep .ant-timeline-item-tail {
  border-left: 2px solid #e8e8e8;
}

::v-deep .ant-timeline-item-head {
  border-color: #1890ff;
  background-color: #1890ff;
  box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.12);
}

// 头部行：时间 + 标签 + 回复按钮
.tl-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 13px;
}

.tl-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tl-time-icon {
  display: inline-block;
  width: 14px;
  height: 14px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23bfbfbf'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z'/%3E%3C/svg%3E") no-repeat center;
  background-size: contain;
  flex-shrink: 0;
}

.tl-time {
  color: #8c8c8c;
  font-size: 13px;
  font-weight: 500;
}

.tl-category {
  display: inline-block;
  padding: 2px 10px;
  font-size: 12px;
  line-height: 20px;
  border-radius: 10px;
  font-weight: 500;
}

.tl-category-daily {
  background: #e6f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.tl-category-plan {
  background: #fff7e6;
  color: #fa8c16;
  border: 1px solid #ffd591;
}

.tl-reply-btn {
  font-size: 13px;
  color: #1890ff;
  padding: 2px 12px;
  border-radius: 4px;
  transition: all 0.3s;

  &:hover {
    color: #40a9ff;
    background: #e6f7ff;
  }
}

// 内容卡片
.tl-card {
  display: flex;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fff;
  padding: 0;
  margin-top: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: box-shadow 0.3s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.tl-card-accent {
  width: 3px;
  background: linear-gradient(180deg, #1890ff 0%, #69c0ff 100%);
  flex-shrink: 0;
}

.tl-card-body {
  flex: 1;
  padding: 14px 16px;
  font-size: 13px;
  color: #262626;
}

.tl-author-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.tl-author-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1890ff 0%, #69c0ff 100%);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.tl-author-name {
  font-size: 14px;
  font-weight: 600;
  color: #262626;
}

.tl-content-line {
  margin-bottom: 6px;
  line-height: 1.6;
  font-size: 13px;
  color: #595959;
}

.tl-content-text {
  color: #595959;
}

.tl-content-body {
  margin-top: 8px;
  padding-top: 8px;
  line-height: 1.8;
  color: #262626;
}

.tl-info-row {
  display: flex;
  align-items: baseline;
  margin-bottom: 4px;
  gap: 6px;
}

.tl-info-label {
  color: #8c8c8c;
  font-size: 12px;
  flex-shrink: 0;
  min-width: 72px;
}

.tl-info-value {
  color: #262626;
  font-size: 13px;
}

.tl-files {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tl-file-tag {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  color: #1890ff;
  text-decoration: none;
  transition: color 0.3s;

  &:hover {
    color: #40a9ff;
    text-decoration: underline;
  }

  .file-icon {
    margin-right: 4px;
    font-size: 13px;
  }
}

.tl-card-status {
  flex-shrink: 0;
  margin-left: 16px;
  padding: 14px 16px;
  border-left: 1px solid #f0f0f0;
  color: #8c8c8c;
  font-size: 13px;
  line-height: 1.6;
  align-self: stretch;
  display: flex;
  align-items: center;
}

// 回复列表
.tl-replies {
  margin-top: 10px;
  padding-left: 16px;
  border-left: 2px solid #e8e8e8;
}

.tl-reply-item {
  margin-bottom: 10px;
  padding: 10px 12px;
  background: #fafafa;
  border-radius: 6px;
  transition: background 0.3s;

  &:hover {
    background: #f0f5ff;
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.tl-reply-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.reply-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #d9d9d9;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.reply-info {
  flex: 1;
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
}

.reply-user {
  font-weight: 600;
  font-size: 13px;
  color: #262626;
}

.reply-target {
  font-size: 12px;
  color: #8c8c8c;
}

.reply-time {
  font-size: 12px;
  color: #bfbfbf;
}

.reply-content {
  font-size: 13px;
  color: #595959;
  line-height: 1.6;
  white-space: pre-wrap;
  padding-left: 30px;
}

.reply-action {
  font-size: 12px;
  color: #1890ff;
  padding: 2px 8px;
  border-radius: 4px;
  transition: all 0.3s;
  flex-shrink: 0;

  &:hover {
    color: #40a9ff;
    background: #e6f7ff;
  }
}

// 回复输入框
.tl-reply-form {
  margin-top: 10px;
  padding: 12px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
}

.tl-reply-target {
  font-size: 12px;
  color: #1890ff;
  margin-bottom: 6px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.reply-target-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #e6f7ff;
  color: #1890ff;
  font-size: 11px;
  font-weight: 700;
}

.reply-textarea {
  ::v-deep .ant-input {
    border-radius: 6px;
    font-size: 13px;

    &:focus {
      border-color: #1890ff;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
    }
  }
}

.reply-form-actions {
  margin-top: 8px;
  display: flex;
  gap: 8px;
}

.reply-cancel-btn {
  border-radius: 6px;
}
</style>
